import { useQuery } from "@tanstack/react-query";
import { BackofficeAPI } from "../api/backoffice";
import type {
  AdminEntityRegistry,
  AdminEntitySchema,
  AdminRow,
  AdminSearchOptions,
  AdminSearchResult,
  GlobalSearchResponse,
  TenantDetail,
} from "../types";

/** Query keys of the back-office's generic layer, in one place. */
export const backofficeKeys = {
  all: ["backoffice"] as const,
  registry: () => [...backofficeKeys.all, "registry"] as const,
  schema: (table: string) => [...backofficeKeys.all, "schema", table] as const,
  rows: (table: string, options: AdminSearchOptions) =>
    [...backofficeKeys.all, "rows", table, options] as const,
  row: (table: string, id: string) =>
    [...backofficeKeys.all, "row", table, id] as const,
  history: (table: string, id: string) =>
    [...backofficeKeys.all, "history", table, id] as const,
  search: (query: string, clientId?: string, includeDeleted = false) =>
    [
      ...backofficeKeys.all,
      "search",
      query,
      clientId ?? null,
      includeDeleted,
    ] as const,
  tenant: (clientId: string) =>
    [...backofficeKeys.all, "tenant", clientId] as const,
};

/** The list of browsable entities. Changes only when the code does. */
export function useEntityRegistry() {
  return useQuery<AdminEntityRegistry>({
    queryKey: backofficeKeys.registry(),
    queryFn: BackofficeAPI.getRegistry,
    staleTime: 30 * 60 * 1000,
  });
}

/** One table's columns. Cached hard for the same reason. */
export function useEntitySchema(table?: string) {
  return useQuery<AdminEntitySchema>({
    queryKey: backofficeKeys.schema(table || ""),
    queryFn: () => BackofficeAPI.getSchema(table as string),
    enabled: !!table,
    staleTime: 30 * 60 * 1000,
    retry: false,
  });
}

/** One page of rows. `enabled` is false until the schema settled the sort. */
export function useEntityRows(
  table: string | undefined,
  options: AdminSearchOptions,
  enabled = true,
) {
  return useQuery<AdminSearchResult>({
    queryKey: backofficeKeys.rows(table || "", options),
    queryFn: () => BackofficeAPI.search(table as string, options),
    enabled: !!table && enabled,
    staleTime: 15 * 1000,
    placeholderData: (previous) => previous,
  });
}

/** One row. */
export function useEntityRow(table?: string, id?: string) {
  return useQuery<AdminRow>({
    queryKey: backofficeKeys.row(table || "", id || ""),
    queryFn: () => BackofficeAPI.getRow(table as string, id as string),
    enabled: !!table && !!id,
    retry: false,
  });
}

/**
 * The audit trail of one row. Returns an empty result for a non-auditable
 * entity, so the panel can be mounted without asking whether it applies.
 */
export function useEntityHistory(table?: string, id?: string, limit = 50) {
  return useQuery<AdminSearchResult>({
    queryKey: backofficeKeys.history(table || "", id || ""),
    queryFn: () =>
      BackofficeAPI.getHistory(table as string, id as string, { limit }),
    enabled: !!table && !!id,
    staleTime: 15 * 1000,
  });
}

/**
 * One term across every searchable entity.
 *
 * Below two characters the server returns nothing anyway, and firing the fan-out
 * on the first keystroke is how a search bar becomes a load generator — so the
 * query simply stays disabled until the term is worth running.
 */
export function useGlobalSearch(
  query: string,
  clientId?: string,
  includeDeleted = false,
) {
  const trimmed = query.trim();

  return useQuery<GlobalSearchResponse>({
    queryKey: backofficeKeys.search(trimmed, clientId, includeDeleted),
    queryFn: () =>
      BackofficeAPI.globalSearch(trimmed, {
        client_id: clientId,
        include_deleted: includeDeleted || undefined,
      }),
    enabled: trimmed.length >= 2,
    staleTime: 30 * 1000,
    placeholderData: (previous) => previous,
  });
}

/** Everything about one tenant, for its detail page. */
export function useTenantDetail(clientId?: string) {
  return useQuery<TenantDetail>({
    queryKey: backofficeKeys.tenant(clientId || ""),
    queryFn: () => BackofficeAPI.getTenantDetail(clientId as string),
    enabled: !!clientId,
    staleTime: 30 * 1000,
  });
}
